import api from '../api';

// ===== TYPE DEFINITIONS =====

export interface Asset {
  id: number;
  tenant_id: number;
  asset_name: string;
  asset_slug: string;
  asset_type: 'infrastructure' | 'application' | 'data' | 'service' | 'code_repository';
  classification: 'critical' | 'important' | 'low_risk';
  description: string | null;
  owner_user_id: number | null;
  owner_name: string | null;
  owner_email: string | null;
  location: string | null;
  location_details: Record<string, unknown>;
  data_stored: string | null;
  data_types: string[];
  access_controls: string | null;
  access_control_details: Record<string, unknown>;
  encryption_status: boolean;
  encryption_details: string | null;
  encryption_at_rest: string | null;
  encryption_in_transit: string | null;
  backup_status: boolean;
  backup_frequency: string | null;
  backup_retention: string | null;
  backup_details: string | null;
  last_review_date: string | null;
  next_review_date: string | null;
  review_frequency_months: number;
  review_status: 'current' | 'due_soon' | 'overdue' | 'pending' | 'in_progress';
  vendor_id: number | null;
  vendor_name: string | null;
  compliance_requirements: string[];
  special_notes: string | null;
  byod_policy_applicable: boolean;
  tags: string[];
  metadata: Record<string, unknown>;
  is_active: boolean;
  archived_at: string | null;
  archived_by: number | null;
  archive_reason: string | null;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
  reviews?: AssetReview[];
  alerts?: AssetAlert[];
  owner?: { id: number; name: string; email: string };
}

export interface AssetDependency {
  id: number;
  tenant_id: number;
  package_name: string;
  package_version: string | null;
  package_type: 'npm' | 'pip' | 'gem' | 'composer' | 'maven' | 'nuget' | 'go' | 'other';
  source: string;
  source_file: string | null;
  is_dev_dependency: boolean;
  is_direct_dependency: boolean;
  license_type: string | null;
  license_spdx: string | null;
  is_copyleft: boolean;
  license_risk: 'high' | 'medium' | 'low' | 'unknown';
  latest_version: string | null;
  is_outdated: boolean;
  versions_behind: number;
  is_deprecated: boolean;
  deprecation_message: string | null;
  eol_status: 'active' | 'maintenance' | 'deprecated' | 'eol';
  eol_date: string | null;
  vulnerability_count: number;
  critical_vulnerabilities: number;
  high_vulnerabilities: number;
  medium_vulnerabilities: number;
  low_vulnerabilities: number;
  vulnerabilities: unknown[];
  last_scan_date: string | null;
  scan_source: string | null;
  scan_details: Record<string, unknown>;
  homepage_url: string | null;
  repository_url: string | null;
  description: string | null;
  notes: string | null;
  detected_at: string;
  created_at: string;
  updated_at: string;
}

export interface AssetReview {
  id: number;
  tenant_id: number;
  asset_id: number;
  review_type: 'quarterly' | 'annual' | 'ad_hoc' | 'incident_triggered' | 'onboarding';
  review_date: string;
  reviewed_by: number | null;
  reviewer_name: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  access_controls_verified: boolean;
  data_classification_confirmed: boolean;
  encryption_verified: boolean;
  backup_verified: boolean;
  changes_documented: boolean;
  findings: string | null;
  action_items: ActionItem[];
  action_items_completed: number;
  action_items_total: number;
  risk_changes: string | null;
  next_review_date: string | null;
  approved_by: number | null;
  approved_at: string | null;
  approval_notes: string | null;
  attachments: unknown[];
  metadata: Record<string, unknown>;
  notes: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  asset?: { id: number; asset_name: string; classification: string };
}

export interface ActionItem {
  id: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  due_date?: string;
  assigned_to?: string;
}

export interface AssetAlert {
  id: number;
  tenant_id: number;
  asset_id: number | null;
  dependency_id: number | null;
  alert_type: 'review_due' | 'review_overdue' | 'eol_warning' | 'eol_expired' |
              'vulnerability_critical' | 'vulnerability_high' | 'license_risk' |
              'dependency_outdated' | 'encryption_missing' | 'backup_missing';
  alert_severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  alert_title: string;
  alert_message: string;
  alert_details: Record<string, unknown>;
  due_date: string | null;
  days_until_due: number | null;
  status: 'active' | 'acknowledged' | 'resolved' | 'snoozed' | 'dismissed';
  acknowledged_by: number | null;
  acknowledged_at: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  snoozed_until: string | null;
  snooze_count: number;
  notification_sent: boolean;
  notification_sent_at: string | null;
  notification_channels: string[];
  auto_generated: boolean;
  created_at: string;
  updated_at: string;
  asset?: { id: number; asset_name: string; classification: string };
  dependency?: { id: number; package_name: string; package_version: string };
}

export interface AssetBYODPolicy {
  id: number;
  tenant_id: number;
  policy_name: string;
  policy_version: string;
  effective_date: string;
  policy_summary: string | null;
  security_model: string | null;
  enforcement_details: string | null;
  cloud_first_enabled: boolean;
  local_data_storage_allowed: boolean;
  mfa_required: boolean;
  mdm_required: boolean;
  session_timeout_minutes: number;
  approved_data_locations: string[];
  metadata: Record<string, unknown>;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface AssetStats {
  total: number;
  byClassification: {
    critical: number;
    important: number;
    low_risk: number;
  };
  byType: Record<string, number>;
  byReviewStatus: {
    current: number;
    due_soon: number;
    overdue: number;
    pending: number;
  };
  encryption: {
    encrypted: number;
    not_encrypted: number;
  };
  backup: {
    has_backup: number;
    no_backup: number;
  };
}

export interface DependencyStats {
  total: number;
  bySource: Record<string, number>;
  byType: Record<string, number>;
  vulnerabilities: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    packages_affected: number;
  };
  licenses: {
    copyleft: number;
    high_risk: number;
    medium_risk: number;
    low_risk: number;
    unknown: number;
  };
  eol_status: {
    active: number;
    maintenance: number;
    deprecated: number;
    eol: number;
  };
  outdated: number;
  dev_dependencies: number;
  production_dependencies: number;
}

export interface AlertSummary {
  total: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  byType: Record<string, number>;
  byStatus: {
    active: number;
    acknowledged: number;
  };
  overdue: number;
  dueSoon: number;
}

export interface AssetFilters {
  asset_type?: string;
  classification?: string;
  review_status?: string;
  is_active?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface DependencyFilters {
  package_type?: string;
  source?: string;
  eol_status?: string;
  license_risk?: string;
  is_outdated?: boolean;
  is_deprecated?: boolean;
  is_copyleft?: boolean;
  has_vulnerabilities?: boolean;
  search?: string;
  sort_by?: string;
}

export interface CreateAssetData {
  asset_name: string;
  asset_type: 'infrastructure' | 'application' | 'data' | 'service' | 'code_repository';
  classification: 'critical' | 'important' | 'low_risk';
  description?: string;
  owner_name?: string;
  owner_email?: string;
  location?: string;
  data_stored?: string;
  data_types?: string[];
  access_controls?: string;
  encryption_status?: boolean;
  encryption_details?: string;
  encryption_at_rest?: string;
  encryption_in_transit?: string;
  backup_status?: boolean;
  backup_frequency?: string;
  backup_retention?: string;
  backup_details?: string;
  review_frequency_months?: number;
  vendor_name?: string;
  compliance_requirements?: string[];
  special_notes?: string;
  tags?: string[];
}

export interface CreateReviewData {
  review_type: 'quarterly' | 'annual' | 'ad_hoc' | 'incident_triggered' | 'onboarding';
  review_date?: string;
  findings?: string;
  notes?: string;
}

export interface CompleteReviewData {
  access_controls_verified: boolean;
  data_classification_confirmed: boolean;
  encryption_verified: boolean;
  backup_verified: boolean;
  changes_documented: boolean;
  findings?: string;
  action_items?: ActionItem[];
  approval_notes?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

// ===== API CLASS =====

class AssetAPI {
  // ===== STATS =====

  async getStats(): Promise<AssetStats> {
    const response = await api.get<ApiResponse<AssetStats>>('/api/assets/stats');
    return response.data.data;
  }

  // ===== ASSETS CRUD =====

  async getAssets(filters?: AssetFilters): Promise<Asset[]> {
    const response = await api.get<ApiResponse<Asset[]>>('/api/assets', { params: filters });
    return response.data.data;
  }

  async getAssetById(id: number): Promise<Asset> {
    const response = await api.get<ApiResponse<Asset>>(`/api/assets/${id}`);
    return response.data.data;
  }

  async createAsset(data: CreateAssetData): Promise<Asset> {
    const response = await api.post<ApiResponse<Asset>>('/api/assets', data);
    return response.data.data;
  }

  async updateAsset(id: number, data: Partial<CreateAssetData>): Promise<Asset> {
    const response = await api.put<ApiResponse<Asset>>(`/api/assets/${id}`, data);
    return response.data.data;
  }

  async archiveAsset(id: number, reason: string): Promise<Asset> {
    const response = await api.post<ApiResponse<Asset>>(`/api/assets/${id}/archive`, { reason });
    return response.data.data;
  }

  async deleteAsset(id: number): Promise<void> {
    await api.delete(`/api/assets/${id}`);
  }

  // ===== REVIEWS =====

  async getAllReviews(filters?: { status?: string; review_type?: string }): Promise<AssetReview[]> {
    const response = await api.get<ApiResponse<AssetReview[]>>('/api/assets/reviews/all', { params: filters });
    return response.data.data;
  }

  async getReviews(assetId: number, filters?: { status?: string; review_type?: string }): Promise<AssetReview[]> {
    const response = await api.get<ApiResponse<AssetReview[]>>(`/api/assets/${assetId}/reviews`, { params: filters });
    return response.data.data;
  }

  async createReview(assetId: number, data: CreateReviewData): Promise<AssetReview> {
    const response = await api.post<ApiResponse<AssetReview>>(`/api/assets/${assetId}/reviews`, data);
    return response.data.data;
  }

  async updateReview(reviewId: number, data: Partial<AssetReview>): Promise<AssetReview> {
    const response = await api.put<ApiResponse<AssetReview>>(`/api/assets/reviews/${reviewId}`, data);
    return response.data.data;
  }

  async completeReview(reviewId: number, data: CompleteReviewData): Promise<AssetReview> {
    const response = await api.put<ApiResponse<AssetReview>>(`/api/assets/reviews/${reviewId}/complete`, data);
    return response.data.data;
  }

  // ===== DEPENDENCIES =====

  async getDependencies(filters?: DependencyFilters): Promise<AssetDependency[]> {
    const response = await api.get<ApiResponse<AssetDependency[]>>('/api/assets/dependencies/all', { params: filters });
    return response.data.data;
  }

  async getDependencyStats(): Promise<DependencyStats> {
    const response = await api.get<ApiResponse<DependencyStats>>('/api/assets/dependencies/stats');
    return response.data.data;
  }

  async scanDependencies(): Promise<{ total_scanned: number; backend: { scanned: number }; frontend: { scanned: number } }> {
    const response = await api.post<ApiResponse<{ total_scanned: number; backend: { scanned: number }; frontend: { scanned: number } }>>('/api/assets/dependencies/scan');
    return response.data.data;
  }

  async deleteDependency(id: number): Promise<void> {
    await api.delete(`/api/assets/dependencies/${id}`);
  }

  async clearDependencies(source?: string): Promise<void> {
    await api.delete('/api/assets/dependencies/clear', { params: { source } });
  }

  // ===== ALERTS =====

  async getAlerts(filters?: { status?: string; alert_severity?: string; alert_type?: string }): Promise<AssetAlert[]> {
    const response = await api.get<ApiResponse<AssetAlert[]>>('/api/assets/alerts/all', { params: filters });
    return response.data.data;
  }

  async getAlertSummary(): Promise<AlertSummary> {
    const response = await api.get<ApiResponse<AlertSummary>>('/api/assets/alerts/summary');
    return response.data.data;
  }

  async generateAlerts(): Promise<{ generated: number; alerts: AssetAlert[] }> {
    const response = await api.post<ApiResponse<{ generated: number; alerts: AssetAlert[] }>>('/api/assets/alerts/generate');
    return response.data.data;
  }

  async acknowledgeAlert(alertId: number): Promise<AssetAlert> {
    const response = await api.post<ApiResponse<AssetAlert>>(`/api/assets/alerts/${alertId}/acknowledge`);
    return response.data.data;
  }

  async resolveAlert(alertId: number, resolutionNotes: string): Promise<AssetAlert> {
    const response = await api.post<ApiResponse<AssetAlert>>(`/api/assets/alerts/${alertId}/resolve`, { resolution_notes: resolutionNotes });
    return response.data.data;
  }

  async snoozeAlert(alertId: number, snoozeUntil: string): Promise<AssetAlert> {
    const response = await api.post<ApiResponse<AssetAlert>>(`/api/assets/alerts/${alertId}/snooze`, { snooze_until: snoozeUntil });
    return response.data.data;
  }

  async sendReminders(): Promise<{ sent: number; total: number }> {
    const response = await api.post<ApiResponse<{ sent: number; total: number }>>('/api/assets/alerts/send-reminders');
    return response.data.data;
  }

  // ===== BYOD POLICY =====

  async getBYODPolicy(): Promise<AssetBYODPolicy> {
    const response = await api.get<ApiResponse<AssetBYODPolicy>>('/api/assets/byod-policy');
    return response.data.data;
  }

  async updateBYODPolicy(data: Partial<AssetBYODPolicy>): Promise<AssetBYODPolicy> {
    const response = await api.put<ApiResponse<AssetBYODPolicy>>('/api/assets/byod-policy', data);
    return response.data.data;
  }

  // ===== AUDIT LOG =====

  async getAuditLog(filters?: { action?: string; entity_type?: string; limit?: number; offset?: number }): Promise<unknown[]> {
    const response = await api.get<ApiResponse<unknown[]>>('/api/assets/audit-log/all', { params: filters });
    return response.data.data;
  }

  async getAssetAuditLog(assetId: number, filters?: { limit?: number; offset?: number }): Promise<unknown[]> {
    const response = await api.get<ApiResponse<unknown[]>>(`/api/assets/${assetId}/audit-log`, { params: filters });
    return response.data.data;
  }

  // ===== EXPORTS =====

  async exportPDF(options?: { classification?: string; include_dependencies?: boolean; include_byod?: boolean }): Promise<Blob> {
    const response = await api.get('/api/assets/export/pdf', {
      params: options,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportExcel(options?: { classification?: string; include_dependencies?: boolean; include_byod?: boolean }): Promise<Blob> {
    const response = await api.get('/api/assets/export/excel', {
      params: options,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportSBOM(options?: { format?: 'json' | 'xml'; source?: 'backend' | 'frontend'; include_dev?: boolean }): Promise<Blob> {
    const response = await api.get('/api/assets/export/sbom', {
      params: options,
      responseType: 'blob'
    });
    return response.data;
  }

  // Helper to download exports
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const assetAPI = new AssetAPI();
export default assetAPI;
